import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Building2, Copy, Check } from 'lucide-react';
import { trackEvent, EVENTS } from '../lib/analytics';
import { getLandingPath } from '../utils/navPrefs';

const EQUIPR_ICON = "/equipr-icon.png";

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const userData = await login(email, password);
      trackEvent(EVENTS.LOGIN, { method: 'email', surface: 'web' });
      // Field users (mechanics) belong on the mobile app — route them there
      // even when they accidentally log in on a desktop browser.
      if (userData?.role === 'mechanic') {
        toast.success('Welcome back! Redirecting to mobile workspace…');
        navigate('/mobile');
      } else {
        toast.success('Welcome back!');
        navigate(getLandingPath());
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-background min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="border-border bg-card/95 backdrop-blur-sm relative z-10">
          {/* Logo, Name & Tagline inside card */}
          <div className="flex flex-col items-center pt-8 pb-4">
            <img src={EQUIPR_ICON} alt="EQUIPR" className="w-16 h-16 rounded-xl mb-4" />
            <h1 className="equipr-logo text-2xl font-bold tracking-tight text-white">
              EQUIPR GUARDIAN
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">Protect Your Assets. Power Your Operations.</p>
          </div>
          
          <CardContent className="pt-2">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="uppercase text-xs tracking-wider">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@company.com"
                  className="input-industrial"
                  required
                  autoFocus
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="uppercase text-xs tracking-wider">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-primary hover:underline"
                    data-testid="login-forgot-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="input-industrial pr-12"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full h-12 btn-industrial text-lg"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                {loading ? 'Signing in...' : 'Login'}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-muted-foreground text-sm">
                New to Guardian?{' '}
                <Link to="/test-drive" className="text-primary hover:underline font-medium" data-testid="login-getting-started-link">
                  Getting Started
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company_name: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registeredClientId, setRegisteredClientId] = useState(null);
  const [copied, setCopied] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCopyClientId = () => {
    navigator.clipboard.writeText(registeredClientId);
    setCopied(true);
    toast.success('Client ID copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const userData = await register({
        ...formData,
        role: 'admin',
      });
      trackEvent(EVENTS.SIGN_UP, { method: 'register_form' });
      setRegisteredClientId(userData.client_id);
      toast.success('Account created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    navigate('/dashboard');
  };

  // Show Client ID confirmation screen after registration
  if (registeredClientId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8">
            <img src={EQUIPR_ICON} alt="EQUIPR" className="w-20 h-20 rounded-2xl mb-4" />
            <h1 className="equipr-logo text-3xl font-bold tracking-tight">
              E<span className="q-accent">Q</span>UIPR
            </h1>
            <p className="text-muted-foreground mt-1">Guardian</p>
          </div>
          
          <Card className="border-primary bg-card">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl uppercase tracking-wide text-green-400">
                Registration Complete!
              </CardTitle>
              <CardDescription>Save your Client ID for future logins</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-6 bg-secondary rounded-lg text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Your Client ID</p>
                <div className="flex items-center justify-center gap-3">
                  <span className="text-4xl font-mono font-bold text-primary tracking-wider">
                    {registeredClientId}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleCopyClientId}
                    className="shrink-0"
                    data-testid="copy-clientid-btn"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="p-4 bg-yellow-900/20 border border-yellow-900/50 rounded-lg">
                <p className="text-sm text-yellow-400">
                  <strong>Important:</strong> Share this Client ID with your team members so they can log in to your organization's account.
                </p>
              </div>
              
              <Button
                onClick={handleContinue}
                className="w-full h-12 btn-industrial text-lg"
                data-testid="continue-to-dashboard-btn"
              >
                Continue to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img src={EQUIPR_ICON} alt="EQUIPR" className="w-20 h-20 rounded-2xl mb-4" />
          <h1 className="equipr-logo text-3xl font-bold tracking-tight">
            E<span className="q-accent">Q</span>UIPR
          </h1>
          <p className="text-muted-foreground mt-1">Guardian</p>
        </div>
        
        <Card className="border-border bg-card">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl uppercase tracking-wide">Create Account</CardTitle>
            <CardDescription>Register your company to get started</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="company_name" className="uppercase text-xs tracking-wider">Company Name</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  placeholder="Your Company Ltd"
                  className="input-industrial"
                  required
                  data-testid="register-company-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="name" className="uppercase text-xs tracking-wider">Your Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  className="input-industrial"
                  required
                  data-testid="register-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="uppercase text-xs tracking-wider">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@yourcompany.com.au"
                  className="input-industrial"
                  required
                  data-testid="register-email-input"
                />
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Use your company email. Free providers (Gmail, Hotmail, Outlook, etc.) are
                  not accepted for new workspaces. If your only address is personal,
                  ask a colleague to invite you as a Mechanic instead.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="uppercase text-xs tracking-wider">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="input-industrial pr-12"
                    required
                    minLength={6}
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full h-12 btn-industrial text-lg"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-muted-foreground text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:underline font-medium">
                  Login
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
